import { fetchJSON as fetchAPI } from './client';

export type DashboardSummary = {
  tenant_id: string;
  workspace_id?: string;
  pending_releases: number;
  today_releases: number;
  scheduled_releases: number;
  failed_releases: number;
  rollback_count: number;
  pending_approvals: number;
  publishing_success_rate: number;
  avg_approval_hours: number;
  avg_publish_duration_ms: number;
  marketplace_health?: Record<string, number>;
  by_stage?: Record<string, number>;
  by_status?: Record<string, number>;
  queue_depth: number;
  unread_notifications: number;
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type ReviewCenterItem = {
  release_id: string;
  release_code: string;
  package_id: string;
  name: string;
  category: string;
  marketplace: string;
  stage: string;
  status: string;
  priority?: string;
  reviewer_id?: string;
  item_count: number;
  asin?: string;
  sku?: string;
  updated_at: string;
};

export type ApprovalCenterItem = {
  approval_id: string;
  release_code?: string;
  package_id: string;
  execution_id: string;
  status: string;
  reviewer_id?: string;
  comment?: string;
  emergency?: boolean;
  level?: number;
  created_at: string;
  updated_at: string;
};

export type EnterpriseComparison = {
  package_id: string;
  release_code?: string;
  marketplace: string;
  field_diffs: { field: string; change_type: string; current?: unknown; proposed?: unknown; item_id?: string }[];
  image_diffs?: { asset_id: string; change_type: string; current_url?: string; proposed_url?: string }[];
  seo_score_delta: number;
  quality_score: number;
  compliance_score: number;
  business_impact: { listings_affected: number; revenue_impact?: number };
  added: number;
  removed: number;
  changed: number;
  generated_at: string;
};

export type PublishingQueueItem = {
  execution_id: string;
  package_id: string;
  release_code?: string;
  name: string;
  marketplace: string;
  status: string;
  mode: string;
  schedule?: string;
  progress: number;
  retry_count: number;
  queued_at: string;
};

export type ReleaseNotes = {
  id: string;
  package_id: string;
  execution_id?: string;
  published_changes: string[];
  seo_improvement?: string;
  compliance_improvement?: string;
  expected_ctr?: number;
  expected_cvr?: number;
  expected_sales_impact?: number;
  expected_ranking_gain?: string;
  generated_at: string;
};

export type ReleasePackage = {
  id: string;
  release_code: string;
  package_id: string;
  name: string;
  marketplace: string;
  stage: string;
  status: string;
  item_count: number;
  created_at: string;
};

export type DeploymentPackage = {
  id: string;
  name: string;
  workspace_id: string;
  marketplace: string;
  status: string;
  content: { type: string; items: { id: string; asin?: string; sku?: string; fields?: Record<string, unknown> }[] };
  created_at: string;
  updated_at: string;
};

export type ActivityEntry = {
  id: string;
  resource_id?: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type ReleaseNotification = {
  id: string;
  title: string;
  body?: string;
  priority: string;
  read: boolean;
  created_at: string;
};

export type BulkOperation = {
  id: string;
  action: string;
  state: string;
  progress: number;
  processed: number;
  failed: number;
  total: number;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_PUBLISHING_API ?? '/api/v1/publishing';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const publishingApi = {
  dashboard: (workspaceId?: string) =>
    fetchJSON<DashboardSummary>(`/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  listPackages: (workspaceId?: string) =>
    fetchJSON<DeploymentPackage[]>(`/packages${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  createPackage: (body: {
    workspace_id: string;
    name: string;
    marketplace?: string;
    content: DeploymentPackage['content'];
  }) => fetchJSON<DeploymentPackage>('/packages', { method: 'POST', body: JSON.stringify(body) }),

  validatePackage: (id: string) =>
    fetchJSON<{ valid: boolean; issues?: { field: string; message: string; severity: string }[] }>(
      `/packages/${id}/validate`,
      { method: 'POST' },
    ),

  validateQuality: (id: string) =>
    fetchJSON<{ valid: boolean; issues?: { field: string; message: string; severity: string }[] }>(
      `/packages/${id}/validate/quality`,
      { method: 'POST' },
    ),

  previewPackage: (id: string) => fetchJSON<unknown>(`/packages/${id}/preview`, { method: 'POST' }),

  compare: (packageId: string) => fetchJSON<EnterpriseComparison>(`/packages/${packageId}/compare`),

  reviewCenter: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return fetchJSON<{ items: ReviewCenterItem[]; total: number }>(`/review${qs}`);
  },

  approvalCenter: (workspaceId?: string) =>
    fetchJSON<ApprovalCenterItem[]>(`/approvals/center${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  bulkApprove: (body: { approval_ids: string[]; reviewer_id: string; comment?: string }) =>
    fetchJSON<ApprovalCenterItem[]>('/approvals/bulk', { method: 'POST', body: JSON.stringify(body) }),

  createRelease: (body: { package_id: string; workspace_id: string; brand_id?: string }) =>
    fetchJSON<ReleasePackage>('/releases', { method: 'POST', body: JSON.stringify(body) }),

  schedule: (body: { package_id: string; mode?: string; preset?: string; schedule_at?: string; timezone?: string }) =>
    fetchJSON<{ id: string; status: string }>('/schedule', { method: 'POST', body: JSON.stringify(body) }),

  deploy: (body: { package_id: string; mode?: string; schedule?: string }) =>
    fetchJSON<{ id: string; status: string }>('/deployments', { method: 'POST', body: JSON.stringify(body) }),

  publishingQueue: (workspaceId?: string) =>
    fetchJSON<PublishingQueueItem[]>(`/queue${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  releaseNotes: (packageId: string) => fetchJSON<ReleaseNotes>(`/packages/${packageId}/release-notes`),

  verify: (executionId: string) =>
    fetchJSON<{ status: string; accepted: boolean }>(`/deployments/${executionId}/verify`, { method: 'POST' }),

  rollback: (body: { execution_id: string; to_version: number }) =>
    fetchJSON<unknown>('/rollback', { method: 'POST', body: JSON.stringify(body) }),

  globalSearch: (body: { text: string; workspace_id?: string }) =>
    fetchJSON<{ packages?: DeploymentPackage[]; total: number }>('/search/global', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listActivity: (limit = 20) => fetchJSON<ActivityEntry[]>(`/activity?limit=${limit}`),

  listNotifications: (unread = true) => fetchJSON<ReleaseNotification[]>(`/notifications?unread=${unread}`),

  markNotificationRead: (id: string) =>
    fetchJSON<{ status: string }>(`/notifications/${id}/read`, { method: 'POST' }),

  triggerBulk: (body: { action: string; package_ids: string[]; workspace_id: string }) =>
    fetchJSON<BulkOperation>('/bulk', { method: 'POST', body: JSON.stringify(body) }),

  diagnostics: () => fetchJSON<Record<string, unknown>>('/diagnostics'),
};

export const BULK_ACTIONS = ['validate', 'approve', 'publish', 'rollback'] as const;
